import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FileText, Download, Ban, AlertTriangle, Loader2 } from "lucide-react"
import { api } from "@/lib/api"
import { useAuth, hasRole } from "@/contexts/AuthContext"
import { toast } from "sonner"

export default function CollectionReceiptPreviewModal({
  receipt,
  open,
  onOpenChange,
  onReceiptVoided,
  isMobile = false,
}) {
  const { user } = useAuth()
  const isAdmin = hasRole(user, "admin")

  const [blobUrl, setBlobUrl] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [errorPdf, setErrorPdf] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const [voidDialogOpen, setVoidDialogOpen] = useState(false)
  const [voidReason, setVoidReason] = useState("")
  const [voiding, setVoiding] = useState(false)

  useEffect(() => {
    let active = true
    let currentBlobUrl = null

    if (open && receipt?.id) {
      setLoadingPdf(true)
      setErrorPdf(null)

      api
        .get(`/collection-receipts/${receipt.id}/pdf`, { responseType: "blob" })
        .then((res) => {
          if (!active) return
          currentBlobUrl = URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" })
          )
          setBlobUrl(currentBlobUrl)
        })
        .catch((err) => {
          if (!active) return
          console.error("PDF load failed", err)
          setErrorPdf("Makbuz PDF yüklenemedi. Lütfen tekrar deneyiniz.")
        })
        .finally(() => {
          if (active) setLoadingPdf(false)
        })
    } else {
      setBlobUrl(null)
    }

    return () => {
      active = false
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl)
      }
    }
  }, [open, receipt?.id])

  if (!receipt) return null

  const isVoided = receipt.status === "voided"
  const receiptNumber = receipt.receipt_number || "TM"

  const handleDownload = async () => {
    try {
      setDownloading(true)
      const res = await api.get(`/collection-receipts/${receipt.id}/pdf`, {
        params: { download: true },
        responseType: "blob",
      })
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      )
      const a = document.createElement("a")
      a.href = url
      a.download = `Tahsilat_Makbuzu_${receiptNumber}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      toast.error("Makbuz indirilemedi.")
    } finally {
      setDownloading(false)
    }
  }

  const handleVoidSubmit = async (e) => {
    e.preventDefault()
    const trimmed = voidReason.trim()
    if (!trimmed) {
      toast.error("Lütfen iptal nedenini giriniz.")
      return
    }
    if (trimmed.length > 500) {
      toast.error("İptal nedeni en fazla 500 karakter olabilir.")
      return
    }

    setVoiding(true)
    try {
      const res = await api.post(`/collection-receipts/${receipt.id}/void`, {
        reason: trimmed,
      })
      toast.success("Tahsilat makbuzu iptal edildi.")
      setVoidDialogOpen(false)
      setVoidReason("")
      if (onReceiptVoided) {
        onReceiptVoided(res.data)
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Makbuz iptal işlemi başarısız oldu."
      toast.error(msg)
    } finally {
      setVoiding(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] flex flex-col p-6 bg-white border border-[#DCE2E8] shadow-xl">
          <DialogHeader className="shrink-0 pb-3 border-b border-[#DCE2E8]">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-rose-600 shrink-0" />
                <DialogTitle className="text-base font-semibold text-[#171A1F] truncate">
                  Tahsilat Makbuzu — {receiptNumber}
                </DialogTitle>
                {isVoided ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                    İPTAL EDİLDİ
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                    DÜZENLENDİ
                  </span>
                )}
              </div>
            </div>
            <DialogDescription className="text-xs text-slate-500 pt-1">
              A5 Yatay formatında hazırlanmış resmi tahsilat makbuzu.
              {isVoided && receipt.void_reason && (
                <span className="block text-red-600 font-medium pt-0.5">
                  İptal Nedeni: {receipt.void_reason}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-3 min-h-[350px] flex items-center justify-center">
            <div className="w-full h-[65vh] rounded-md overflow-hidden border border-[#DCE2E8] bg-slate-50 shadow-inner flex items-center justify-center">
              {loadingPdf ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 text-xs">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  Makbuz yükleniyor...
                </div>
              ) : errorPdf ? (
                <div className="text-red-500 text-xs text-center p-4">
                  {errorPdf}
                </div>
              ) : blobUrl ? (
                <iframe
                  src={blobUrl}
                  title={`Tahsilat Makbuzu ${receiptNumber}`}
                  className="w-full h-full border-0"
                />
              ) : null}
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-3 border-t border-[#DCE2E8] flex flex-row items-center justify-between gap-2">
            <div>
              {!isMobile && isAdmin && !isVoided && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setVoidDialogOpen(true)}
                  className="text-xs h-9"
                  data-testid="void-receipt-btn"
                >
                  <Ban className="mr-1.5 h-3.5 w-3.5" />
                  Makbuzu İptal Et
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={handleDownload}
                disabled={downloading || loadingPdf}
                className="bg-[#168DEB] hover:bg-[#1272BE] text-white shadow-sm text-xs h-9"
                data-testid="receipt-download-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {downloading ? "İndiriliyor..." : "İndir"}
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-[#DCE2E8] text-slate-700 hover:bg-slate-50 text-xs h-9"
              >
                Kapat
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Void Dialog (desktop only) */}
      {!isMobile && voidDialogOpen && (
        <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
          <DialogContent className="max-w-md w-[90vw] p-6 bg-white border border-[#DCE2E8] shadow-xl">
            <DialogHeader className="pb-3 border-b border-[#DCE2E8]">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                <DialogTitle className="text-base font-semibold text-slate-900">
                  Tahsilat Makbuzunu İptal Et
                </DialogTitle>
              </div>
              <DialogDescription className="text-xs text-slate-500 pt-1">
                Bu işlem geri alınamaz. Makbuz veritabanından silinmez; üzerine İPTAL filigranı eklenir ve denetim kaydına işlenir.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVoidSubmit} className="space-y-4 py-2">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  İptal Nedeni <span className="text-red-500">*</span> (Maksimum 500 karakter)
                </label>
                <textarea
                  required
                  maxLength={500}
                  rows={3}
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Örn: Tahsilat tutarı veya ödeme yöntemi sehven hatalı girildi."
                  className="w-full text-sm rounded-md border border-slate-300 p-2.5 focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  data-testid="void-reason-textarea"
                />
                <div className="text-[11px] text-slate-400 text-right mt-0.5">
                  {voidReason.length}/500
                </div>
              </div>

              <DialogFooter className="pt-2 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setVoidDialogOpen(false)
                    setVoidReason("")
                  }}
                  disabled={voiding}
                  className="text-xs h-9 border-slate-300"
                >
                  Vazgeç
                </Button>
                <Button
                  type="submit"
                  disabled={voiding || !voidReason.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs h-9 shadow-sm"
                  data-testid="confirm-void-receipt-btn"
                >
                  {voiding ? "İptal Ediliyor..." : "Makbuzu İptal Et"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
